import { forwardRef, useEffect, useState } from "react";
import {
  Card,
  Row,
  Col,
  Button,
  Table,
  Tag,
  Upload,
  Flex,
  Tooltip,
  Typography,
  Pagination,
} from "antd";
import { FileAddOutlined, UploadOutlined } from "@ant-design/icons";
import { errorMessage } from "../../../utils/notificationUtils";
import dayjs from "dayjs";
import { Radio } from "antd";
import { useReconciliationSSE } from "../../../hooks/useReconciliationSSE";
import { ROUTES } from "../../../utils/constants";
import { useNavigate } from "react-router-dom";
import { Badge } from "antd";
import ReconFileViewMoreModal from "./ReconFileViewMoreModal";
import {
  fetchProcessedFiles,
  getUploadInputList,
} from "../../../apis/reconciliationService";

const ReconciliationFileSSE = ({
  file,
  onUpdate,
}: {
  file: any;
  onUpdate: any;
}) => {
  useReconciliationSSE({
    fileType: file.fileType?.replace(" ", "_"),
    fileReconId: file.fileReconId,
    fileStatus: file.fileStatus,
    onUpdate,
  });
  return null;
};

interface Props {
  uploadRegister: any;
  setReconciliationFile: any;
  selectedFile: any;
  handleFileChange: any;
  handleRemoveFile: any;
  reconciliationFile: any;
  isFileMappingLoading: boolean;
  isDocumentUploading: boolean;
  documentUrl: string | null;
  loadingProcessedFileList: boolean;
  reconData: any;
  totalListCount: any;
  onPaginationChange: any;
  pagination: any;
  setPagination: any;
  setReconData: any;
  bodEod: any;
  selectedHospital: number | null;
  availableHospitals: { label: string; value: string | number }[];
}

const { Text } = Typography;

const ReconFileUpload = forwardRef<any, Props>(
  ({
    uploadRegister,
    setReconciliationFile,
    selectedFile,
    handleFileChange,
    handleRemoveFile,
    reconciliationFile,
    isFileMappingLoading,
    isDocumentUploading,
    documentUrl,
    reconData,
    totalListCount,
    loadingProcessedFileList,
    onPaginationChange,
    pagination,
    setPagination,
    setReconData,
    bodEod,
    selectedHospital,
    availableHospitals,
  }) => {
    const [openReconFileViewMoreModal, setOpenReconFileViewMoreModal] =
      useState(false);
    const [
      isReconFileViewMoreModalDataLoading,
      setIsReconFileViewMoreModalDataLoading,
    ] = useState<boolean>(false);
    const [reconFileViewMoreModalData, setReconFileViewMoreModalData] =
      useState<any[]>([]);
    const [reconFileViewMoreDataTotal, setReconFileViewMoreDataTotal] =
      useState<number>(0);
    const navigate = useNavigate();
    const [selectedVisitType, setSelectedVisitType] = useState<string>("OP");
    const [availableRegisters, setAvailabelRegisters] = useState<any>([]);
    const [availableRegisterErrorMessage, setAvailableRegisterErrorMessage] =
      useState<any>(null);

    useEffect(() => {
      setAvailableRegisterErrorMessage(null);
      getUploadInput(selectedVisitType);
    }, [selectedVisitType]);

    const fetchReconFileViewMoreModalData = async (payload: any) => {
      try {
        setIsReconFileViewMoreModalDataLoading(true);
        const response = await fetchProcessedFiles(payload);
        setReconFileViewMoreModalData(response?.data || []);
        setReconFileViewMoreDataTotal(response?.totalElements || 0);
      } catch (error: any) {
        const messageText =
          error?.message || "Failed to load process history for View More";
        errorMessage(messageText);
        setReconFileViewMoreModalData([]);
        setReconFileViewMoreDataTotal(0);
      } finally {
        setIsReconFileViewMoreModalDataLoading(false);
      }
    };

    const getUploadInput = async (selectedVisitType: string) => {
      try {
        if (selectedHospital) {
          const response = await getUploadInputList(
            selectedHospital,
            selectedVisitType,
          );
          setAvailabelRegisters(response?.data || []);
        } else {
          setAvailabelRegisters([]);
        }
        setAvailableRegisterErrorMessage(null);
      } catch (error: any) {
        console.error("Error in getting upload list", error);
        if (error?.response?.data?.message) {
          setAvailableRegisterErrorMessage(error?.response?.data?.message);
        } else {
          setAvailableRegisterErrorMessage(null);
        }
        setAvailabelRegisters([]);
      }
    };

    return (
      <>
        {reconData?.map((file: any, idx: any) =>
          file.fileStatus !== "COMPLETED" ? (
            <ReconciliationFileSSE
              key={file.fileReconId}
              file={file}
              onUpdate={(data: any) => {
                setReconData((prev: any) => {
                  const updated = [...prev];
                  updated[idx] = {
                    ...updated[idx],
                    ...data,
                    fileStatus: data.status,
                  };
                  return updated;
                });
              }}
            />
          ) : null,
        )}

        <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
          {/* Register Upload */}
          <Card
            title={
              <span
                style={{ display: "flex", alignItems: "center", gap: "8px" }}
              >
                <FileAddOutlined style={{ color: "rgb(0, 76, 143)" }} />
                Register to Upload
              </span>
            }
          >
            <Row style={{ marginTop: -8, marginBottom: 16 }}>
              <Col>
                <Flex align="center" gap={10}>
                  <Text strong>Visit Type:</Text>.
                  <Radio.Group
                    value={selectedVisitType}
                    onChange={(e) => setSelectedVisitType(e.target.value)}
                  >
                    <Radio value="OP">OP</Radio>
                    <Radio value="IP">IP</Radio>
                  </Radio.Group>
                </Flex>
              </Col>
            </Row>

            <Row gutter={[16, 8]} align="middle">
              <Col>
                {selectedFile ? (
                  <Card
                    hoverable
                    bodyStyle={{
                      padding: "0 12px",
                      height: "40px",
                      display: "flex",
                      alignItems: "center",
                      gap: "10px",
                      overflowX: "auto",
                    }}
                    style={{
                      display: "inline-flex",
                      width: "fit-content",
                      maxWidth: "100%",
                      cursor: "pointer",
                      border: "1px solid #e5e5e5",
                      borderRadius: 10,
                      boxShadow: "0 1px 4px rgba(0,0,0,0.08)",
                    }}
                  >
                    <Text
                      strong
                      style={{
                        whiteSpace: "nowrap",
                        flexShrink: 0,
                      }}
                    >
                      {reconciliationFile?.label}
                    </Text>

                    <Flex
                      align="center"
                      gap={8}
                      style={{
                        flexShrink: 0,
                      }}
                    >
                      <Tooltip title={selectedFile.file.name}>
                        <Text
                          style={{
                            fontSize: 14,
                            whiteSpace: "nowrap",
                          }}
                        >
                          {selectedFile.file.name}
                        </Text>
                      </Tooltip>

                      <Button onClick={handleRemoveFile} danger size="small">
                        Remove
                      </Button>
                    </Flex>
                  </Card>
                ) : (
                  <Row gutter={16}>
                    {availableRegisterErrorMessage && (
                      <Col>
                        <Card
                          bodyStyle={{
                            padding: "0 12px",
                            height: "36px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                          style={{
                            display: "inline-flex",
                            width: "fit-content",
                            maxWidth: "100%",
                            borderRadius: 10,
                            backgroundColor: "#fff1f0", // light red bg
                            border: "1px solid #ffa39e", // red border
                          }}
                        >
                          <Text
                            style={{ color: "#cf1322", whiteSpace: "nowrap" }}
                          >
                            {availableRegisterErrorMessage}
                          </Text>
                        </Card>
                      </Col>
                    )}

                    {availableRegisters?.length > 0 &&
                      availableRegisters.map((register: any) => (
                        <Col key={register?.sourceFileType}>
                          <Card
                            hoverable
                            bodyStyle={{
                              padding: "0 12px",
                              height: "36px",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                              gap: "10px",
                            }}
                            style={{
                              display: "inline-flex",
                              width: "fit-content",
                              maxWidth: "100%",
                              cursor: "pointer",
                              border: "1px solid #e5e5e5",
                              borderRadius: 10,
                              boxShadow: "0 1px 4px rgba(0,0,0,0.08)",
                            }}
                            onClick={() => setReconciliationFile(register)}
                          >
                            <Text strong style={{ whiteSpace: "nowrap" }}>
                              {register?.label?.replace("_", " ")}
                            </Text>

                            <Upload
                              showUploadList={false}
                              beforeUpload={() => false}
                              onChange={(info) => {
                                setReconciliationFile(register);
                                handleFileChange(info);
                              }}
                            >
                              <UploadOutlined
                                style={{ fontSize: 18, color: "#00549D" }}
                              />
                            </Upload>
                          </Card>
                        </Col>
                      ))}
                  </Row>
                )}
              </Col>

              {/* Upload Button */}
              <Col style={{ marginLeft: "10%" }}>
                <Button
                  type="primary"
                  icon={<UploadOutlined />}
                  style={{
                    backgroundColor:
                      isFileMappingLoading ||
                      isDocumentUploading ||
                      !documentUrl
                        ? "#00549D"
                        : "#00549D",
                    borderColor:
                      isFileMappingLoading ||
                      isDocumentUploading ||
                      !documentUrl
                        ? "#00549D"
                        : "#00549D",
                    color: "#fff",
                    opacity:
                      isFileMappingLoading ||
                      isDocumentUploading ||
                      !documentUrl
                        ? 0.6
                        : 1,
                    height: 36,
                  }}
                  onClick={uploadRegister}
                  loading={isFileMappingLoading || isDocumentUploading}
                  disabled={
                    isFileMappingLoading || isDocumentUploading || !documentUrl
                  }
                >
                  Upload
                </Button>
              </Col>
            </Row>
          </Card>

          {/* Recent Activity History */}
          <Card
            title="Recent Activity History"
            extra={
              <a
                onClick={() => {
                  setOpenReconFileViewMoreModal(true);
                }}
              >
                View More
              </a>
            }
          >
            <Table
              pagination={false}
              columns={[
                {
                  title: "Process Type",
                  dataIndex: "eodBodProcessType",
                  render: (value: any) => (value ? value : "-"),
                },
                {
                  title: "Register Type",
                  dataIndex: "fileType",
                  render: (value: any) => (value ? value : "-"),
                },
                {
                  title: "Updated By",
                  dataIndex: "updatedBy",
                  render: (value: any) => (value ? value : "-"),
                },
                {
                  title: "Timestamp",
                  dataIndex: "dateAndTime",
                  render: (_: any, record: any) => {
                    return record?.dateAndTime
                      ? dayjs(record.dateAndTime).format("DD-MM-YYYY hh:mm A")
                      : "-";
                  },
                },
                {
                  title: "Status",
                  dataIndex: "fileStatus",
                  render: (status: string, record: any) => {
                    const isFailed = status?.toLowerCase() === "failed";
                    const isCompleted = status?.toLowerCase() === "completed";

                    const tag = (
                      <Tag
                        color={
                          isCompleted ? "green" : isFailed ? "red" : "orange"
                        }
                        style={{
                          borderRadius: "999px",
                          padding: "2px 12px",
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 6,
                        }}
                      >
                        <Badge
                          color={
                            isCompleted ? "green" : isFailed ? "red" : "orange"
                          }
                        />
                        {status}
                      </Tag>
                    );
                    return isFailed ? (
                      <Tooltip
                        title={record?.errorMessage || "No reason available"}
                      >
                        {tag}
                      </Tooltip>
                    ) : (
                      tag
                    );
                  },
                },
                {
                  title: <span style={{ marginLeft: 70 }}>Action</span>,
                  render: (_: any, record: any) => {
                    return (
                      <Flex gap={8}>
                        <span
                          style={{ cursor: "pointer", color: "#00549D" }}
                          onClick={() =>
                            window.open(
                              `https://view.officeapps.live.com/op/view.aspx?src=${encodeURIComponent(
                                record?.s3Url,
                              )}`,
                              "_blank",
                            )
                          }
                        >
                          View Registers
                        </span>

                        <span
                          style={{
                            cursor: "pointer",
                            color: "#00549D",
                            marginLeft: 10,
                          }}
                          onClick={() => {
                            sessionStorage.setItem("eod-active-tab", "2");
                            sessionStorage.setItem(
                              "selected-bod-eod-value",
                              bodEod,
                            );
                            if (
                              record?.fileType?.toLowerCase()?.includes("op")
                            ) {
                              navigate(`${ROUTES.OP_RECONCILIATION}`);
                            } else {
                              navigate(`${ROUTES.IP_RECONCILIATION}`);
                            }
                          }}
                        >
                          View Result
                        </span>
                      </Flex>
                    );
                  },
                },
              ]}
              dataSource={reconData}
              loading={loadingProcessedFileList}
            />

            <div
              style={{
                borderTop: "1px solid #f0f0f0",
                paddingTop: 20,
                height: 20,
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-end",
              }}
            >
              <Pagination
                size="small"
                current={pagination.current}
                pageSize={pagination.pageSize}
                total={totalListCount}
                showSizeChanger
                pageSizeOptions={["10", "20", "50", "100"]}
                onChange={(page, pageSize) => {
                  setPagination({
                    current: page,
                    pageSize,
                  });
                  onPaginationChange(page, pageSize);
                }}
              />
            </div>
          </Card>

          <ReconFileViewMoreModal
            open={openReconFileViewMoreModal}
            onClose={() => setOpenReconFileViewMoreModal(false)}
            availableHospitals={availableHospitals || []}
            selectedHospital={selectedHospital}
            isReconFileViewMoreModalDataLoading={
              isReconFileViewMoreModalDataLoading
            }
            viewMoreModalData={reconFileViewMoreModalData}
            fetchReconFileViewMoreModalData={fetchReconFileViewMoreModalData}
            viewMoreDataTotal={reconFileViewMoreDataTotal}
          />
        </div>
      </>
    );
  },
);

export default ReconFileUpload;
